import { isFiniteNumber } from "@bitwarden/web-vault/app/shared/utils/helper-number-format";
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  untracked,
  viewChild,
  WritableSignal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { take } from "rxjs/operators";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import {
  CreditsService,
  CreditPack,
  ConversionError,
  ResolvedConversion,
} from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import {
  PURCHASE_ID_KEY,
  PURCHASE_ID_PACK_KEY,
  PURCHASE_ID_TS_KEY,
  GOLD_MULTIPLIER,
  GC_PER_AUD,
} from "@bitwarden/web-vault/app/models/constants/credits.constants";
import {
  GLASS_BUTTON_BG,
  GLASS_BUTTON_SHADOW,
} from "@bitwarden/web-vault/app/models/constants/glass-button.constants";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { DeviceType } from "@bitwarden/common/enums/deviceType";
import {
  AppleConfigService,
  StoreKitProductUnavailableError,
} from "@bitwarden/web-vault/app/services/api/apple-config.service";
import {
  buildCashReceipt,
  buildConvertReceipt,
  openCreditsAddedDialog,
} from "@bitwarden/web-vault/app/gloss/credits/components/credits-added/credits-added.component";
import { DialogWrapperComponent } from "@bitwarden/web-vault/app/components/wrappers/dialog-wrapper/dialog-wrapper.component";
import { ConversionConfirmationComponent } from "@bitwarden/web-vault/app/gloss/credits/components/gold-platinum-converter/components/conversion-confirmation/conversion-confirmation.component";
import { IConversionResponse } from "@bitwarden/web-vault/app/models/interfaces/credits.interfaces";
import type { Transaction } from "@capgo/native-purchases";

export type PurchaseTab = "cash" | "platinum";

@Component({
  selector: "app-points-purchase",
  templateUrl: "./points-purchase.component.html",
  styles: [
    `
      .pp-slider {
        -webkit-appearance: none;
        appearance: none;
        height: 8px;
        border-radius: 9999px;
        outline: none;
      }
      .pp-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 20px;
        height: 20px;
        border-radius: 9999px;
        background: #ffffff;
        cursor: pointer;
        box-shadow:
          -1px -1px 3px 0px rgba(255, 255, 255, 0.55),
          0px 2px 5px 0px rgba(0, 0, 0, 0.14);
      }
      .pp-slider::-moz-range-thumb {
        width: 20px;
        height: 20px;
        border-radius: 9999px;
        background: #ffffff;
        border: none;
        cursor: pointer;
        box-shadow:
          -1px -1px 3px 0px rgba(255, 255, 255, 0.55),
          0px 2px 5px 0px rgba(0, 0, 0, 0.14);
      }
      .pp-slider:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `,
  ],
})
export class PointsPurchaseComponent implements OnInit {
  private creditsService = inject(CreditsService);
  private notifyService = inject(ConfirmationDialogService);
  private platformUtils = inject(PlatformUtilsService);
  private appleConfigService = inject(AppleConfigService);
  private i18n = inject(I18nService);
  private dialog = inject(MatDialog);
  private dialogRef = inject<MatDialogRef<unknown>>(MatDialogRef, { optional: true });
  private destroyRef = inject(DestroyRef);
  private logService = inject(LogService);

  protected readonly goldMultiplier = GOLD_MULTIPLIER;
  protected readonly isIOS = this.platformUtils.getDevice() === DeviceType.iOS;

  /** Tab to open on, passed via DialogWrapper's componentInputs. */
  readonly initialTab = input<PurchaseTab>("cash");
  protected readonly tab: WritableSignal<PurchaseTab> = signal("cash");

  // --- Buy with Cash ---
  protected packs = signal<CreditPack[]>([]);
  protected isLoadingPacks = signal(false);
  protected buyingPackId = signal<string | null>(null);
  protected selectedPack: WritableSignal<CreditPack | null> = signal(null);

  /** Lowest price-per-credit pack is flagged "Best value" — no such field exists on CreditPack. */
  protected readonly bestValueProductId = computed(() => {
    const list = this.packs();
    if (!list.length) {
      return null;
    }
    return list.reduce((best, pack) =>
      pack.price / pack.credits < best.price / best.credits ? pack : best,
    ).productId;
  });

  /** CrG-per-AUD rate for the header badge — same rate shown on the gift-card redemption badge.
   *  Not derived from pack pricing: packs can be discounted/bundled, so their own credits/price
   *  ratio isn't the quoted rate. */
  protected readonly exchangeRate = GC_PER_AUD;

  /** Smallest pack's credit amount, for the "min $MIN$ CrG" purchase caption. */
  protected readonly minPackCredits = computed(() => {
    const list = this.packs();
    return list.length ? Math.min(...list.map((p) => p.credits)) : 0;
  });

  protected readonly newGoldBalancePreview = computed(
    () => this.creditsService.totalGoldCredits() + (this.selectedPack()?.credits ?? 0),
  );

  // --- Use Platinum Credits ---
  private readonly amountEl = viewChild.required<ElementRef<HTMLInputElement>>("amountInput");
  protected readonly quickPercents = [0.25, 0.5, 1] as const;
  protected readonly platinumAmount: WritableSignal<number> = signal(0);
  protected readonly conversionId: WritableSignal<string> = signal("");
  protected readonly converting: WritableSignal<boolean> = signal(false);
  /** This drawer's conversion running, or one with its id from a drawer closed mid-request: the
   *  button reads "processing" either way, rather than a Convert that silently does nothing. */
  protected readonly busy = computed(
    () => this.converting() || this.creditsService.isConversionInFlight(this.conversionId()),
  );
  protected readonly totalPlatinum = computed(() => this.creditsService.totalPlatinumCredits());
  protected readonly hasPlatinumBalance = computed(() => this.totalPlatinum() > 0);
  protected readonly goldPreview = computed(() => this.platinumAmount() * GOLD_MULTIPLIER);
  // Can go negative when the amount exceeds a balance that dropped while the drawer was open
  // (Convert is disabled then, see convertDisabled): shown as-is so the user can see by how much.
  protected readonly balanceAfterConversion = computed(
    () => this.totalPlatinum() - this.platinumAmount(),
  );
  // Also disabled when the amount is more than the balance: the amount is only clamped when it's
  // set, so a balance that drops while the drawer is open (a conversion on another device, then a
  // push-notification sync) would otherwise leave Convert submitting the old amount. Not
  // re-clamped here, which would move the amount under an open confirm dialog (see prefill below).
  // And while a request with this id is in flight from any drawer: one closed mid-request and
  // reopened offers the same id, and a second POST with it ended in two receipts (#6248).
  protected readonly convertDisabled = computed(
    () => this.busy() || this.platinumAmount() <= 0 || this.platinumAmount() > this.totalPlatinum(),
  );
  protected readonly sliderFill = computed(() => {
    const total = this.totalPlatinum();
    const pct = total > 0 ? Math.min(100, (this.platinumAmount() / total) * 100) : 0;
    return (
      `linear-gradient(to right, #00b8b9 0%, var(--button-glass-accent-bg) ${pct}%, ` +
      `var(--color-segmented-container-bg) ${pct}%, var(--color-segmented-container-bg) 100%)`
    );
  });
  /** "You get · Gold" preview box background — same two accent colors as the rest of this tab,
   *  at the low alpha the design gives (10% / 6%). */
  protected readonly youGetGoldBg =
    "linear-gradient(135deg, rgba(0, 196, 196, 0.1) 0%, rgba(0, 128, 129, 0.06) 100%)";
  protected readonly glassButtonBg = GLASS_BUTTON_BG;
  protected readonly glassButtonShadow = GLASS_BUTTON_SHADOW;

  /** Set once the amount has been prefilled, so later balance refreshes never move it. */
  private prefilled = false;
  /** The amount is still the one prefilled from an unresolved conversion (not yet changed). */
  private prefilledFromUnresolved = false;
  /** The confirm dialog is open: runConversion() reads platinumAmount() and conversionId() once
   *  it closes, so neither may move meanwhile (see the committed-id effect). */
  private readonly confirmOpen = signal(false);

  constructor() {
    // Prefill the conversion with the full Platinum balance so the form opens ready to convert,
    // instead of at 0. An effect rather than ngOnInit because the balance may still be loading
    // when the drawer opens. It fills once, the first time a balance arrives: a later refresh
    // (push-notification sync, auto-redemption's fetch) must not move the amount under an open
    // confirm dialog or a retry, since runConversion() reads platinumAmount() again. The user
    // can't pick an amount before this runs: the controls are disabled until hasPlatinumBalance().
    // conversionId is set here too, as setPlatinumAmount() would, so Convert never submits an
    // empty id. A conversion whose outcome is still unknown (the drawer was closed mid-request or
    // after a 5xx) is prefilled instead, with its id, so converting again can't commit twice. It
    // isn't clamped: a balance now below it most likely means it committed, and clamping would
    // mint a new id for a second conversion. Unclamped, Convert stays disabled (amount > balance),
    // as it would have in a drawer left open. untracked() alone allows the writes (same pattern as
    // CreditsService).
    effect(() => {
      const total = this.totalPlatinum();
      if (total > 0 && !this.prefilled) {
        this.prefilled = true;
        untracked(() => {
          const accountId = this.accountId();
          const unresolvedAmount = this.creditsService.unresolvedConversion(accountId);
          this.prefilledFromUnresolved = unresolvedAmount !== null;
          const amount = unresolvedAmount ?? this.clampPlatinumAmount(total);
          this.platinumAmount.set(amount);
          // The unresolved conversion's id when the amount is its (the newest record wins).
          this.conversionId.set(this.creditsService.conversionIdFor(accountId, amount));
        });
      }
    });

    // The id is only picked when the amount is set, so once its conversion is known to have
    // committed - by its own 2xx in a drawer closed mid-request, or found after a 5xx - this
    // drawer would go on offering it, and Convert would replay it: a second receipt, nothing new
    // converted (#6248). Swap in the id the amount gets now (a fresh one). An amount still as
    // prefilled from that conversion's record is reset too, to the full balance as on a normal
    // open: it was only there to retry that conversion, which can be found committed only after
    // the prefill (transactions load after balances, e.g. on a relaunch, #6247).
    // Nothing changes under an open confirm dialog or a running conversion: the user confirmed
    // this amount as a retry of that conversion, and a fresh id would convert it a second time
    // (the old one only replays it). Both are read here, so it catches up once they're done.
    effect(() => {
      const id = this.conversionId();
      if (this.confirmOpen() || this.converting()) {
        return;
      }
      if (id && this.creditsService.isConversionCommitted(id)) {
        untracked(() => {
          if (this.prefilledFromUnresolved) {
            this.prefilledFromUnresolved = false;
            this.platinumAmount.set(this.clampPlatinumAmount(this.totalPlatinum()));
          }
          this.conversionId.set(
            this.creditsService.conversionIdFor(this.accountId(), this.platinumAmount()),
          );
        });
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.setTab(this.initialTab());
    await this.loadPacks();
  }

  private async loadPacks(): Promise<void> {
    this.isLoadingPacks.set(true);
    try {
      const packs = await this.creditsService.listPacks();
      this.packs.set(packs);
      this.selectedPack.set(
        packs.find((p) => p.productId === this.bestValueProductId()) ?? packs[0] ?? null,
      );
    } catch {
      this.notifyService.notify(NotificationEnum.error, "errorOccurred", "purchaseFail");
    } finally {
      this.isLoadingPacks.set(false);
    }
  }

  protected setTab(tab: PurchaseTab): void {
    this.tab.set(tab);
  }

  protected selectPack(pack: CreditPack): void {
    this.selectedPack.set(pack);
  }

  protected continueToPayment(): void {
    const pack = this.selectedPack();
    if (!pack || this.buyingPackId()) {
      return;
    }
    void this.buy(pack);
  }

  /** Platform-dispatch entry point — always routes by device, same as before the "Buy with Cash"
   *  tab existed. The payment-method rows above are informational (Apple Pay only realistically
   *  works via native StoreKit on iOS); they don't override this, so there's no path where
   *  clicking "Stripe" on iOS would silently still charge through StoreKit or vice versa. */
  protected buy(pack: CreditPack): Promise<void> {
    if (this.platformUtils.getDevice() === DeviceType.iOS) {
      return this.appleCheckout(pack);
    }
    return this.stripeCheckout(pack);
  }

  private async stripeCheckout(pack: CreditPack): Promise<void> {
    if (this.buyingPackId()) {
      return;
    }
    this.buyingPackId.set(pack.productId);
    try {
      const { purchaseId, url } = await this.creditsService.createStripeCreditCheckout(
        pack.productId,
      );
      // Id before timestamp: if the id write throws, an older stored id keeps its old timestamp and
      // still expires instead of looking freshly pending.
      localStorage.setItem(PURCHASE_ID_KEY, purchaseId);
      localStorage.setItem(PURCHASE_ID_TS_KEY, new Date().toISOString());
      // Stashed so the "Credits added" receipt can show "Paid {amount} {currency}" on return —
      // PurchaseDetail carries credits/newBalance but never the price the user actually paid.
      // Tagged with the purchase id so the receipt only uses it for that purchase. Optional: if the
      // write throws, the receipt just loses its price line, so the checkout goes on.
      try {
        localStorage.setItem(
          PURCHASE_ID_PACK_KEY,
          JSON.stringify({
            purchaseId,
            price: pack.price,
            currency: pack.currency,
            credits: pack.credits,
          }),
        );
      } catch (err) {
        this.logService.warning(`Couldn't stash the pack for the Stripe receipt: ${err}`);
      }
      window.location.href = url;
      // buyingPackId stays set — page is navigating away.
    } catch {
      this.notifyService.notify(NotificationEnum.error, "errorOccurred", "purchaseFail");
      this.buyingPackId.set(null);
    }
  }

  private async appleCheckout(pack: CreditPack): Promise<void> {
    if (this.buyingPackId()) {
      return;
    }
    this.buyingPackId.set(pack.productId);
    try {
      const { purchaseId, appAccountToken } = await this.creditsService.createAppleCreditCheckout(
        pack.productId,
      );
      let transaction: Transaction | null;
      try {
        transaction = await this.appleConfigService.purchaseCreditPack(
          pack.productId,
          appAccountToken,
        );
      } catch (error) {
        // Before the payment sheet (the product lookup failed): nothing can have been charged,
        // so it's a plain failure - the outer catch toasts purchaseFail and Buy stays up (#6249).
        // Logged here, as the outer catch doesn't: a pack missing from App Store Connect must be
        // findable.
        if (error instanceof StoreKitProductUnavailableError) {
          this.logService.error(`Apple purchase ${purchaseId} failed: ${error.message}`);
          throw error;
        }
        // Not a cancellation (that resolves null): StoreKit can reject after the payment sheet
        // with the user charged (unverified transaction) or about to be (Ask to Buy / SCA
        // pending). Treat it like a "pending" finalizePurchase, not a failure: close the drawer
        // rather than leave Buy up inviting a second payment. Login-recovery reconciles it.
        // Capacitor's bridge rejects plugin calls with an Error (cap.Exception), so it logs as text.
        this.dialogRef?.close();
        this.notifyService.notify(NotificationEnum.info, "processing", "creditsPurchaseProcessing");
        this.logService.error(`Apple purchase ${purchaseId} rejected by StoreKit: ${error}`);
        return;
      }
      if (!transaction) {
        // User cancelled or StoreKit unavailable — no error toast.
        // The backend purchase record stays pending: nothing expires it, and if a charge does
        // happen, the App Store ONE_TIME_CHARGE webhook completes it.
        return;
      }
      const detail = await this.creditsService.finalizePurchase(purchaseId);
      if (detail.status !== "completed") {
        // finalizePurchase has already toasted the failed/pending outcome. Pending may already be
        // charged, so don't leave the Buy buttons up to invite a second payment (same as Stripe).
        if (detail.status === "pending") {
          this.dialogRef?.close();
        }
        return;
      }
      try {
        const goldAdded = isFiniteNumber(detail.credits) ? detail.credits : pack.credits;
        const { subtitle, rows } = buildCashReceipt(this.i18n, {
          goldAdded,
          newGoldBalance: isFiniteNumber(detail.newBalance)
            ? detail.newBalance
            : this.creditsService.totalGoldCredits(),
          // No paidAmount/paidCurrency: pack.price is the backend catalogue figure, but
          // StoreKit charges the App Store Connect tier for the user's storefront, which can
          // differ in both amount and currency. buildCashReceipt falls back to the plain
          // "Credits added" subtitle rather than printing a price we can't vouch for.
          transactionId: purchaseId,
        });
        this.dialogRef?.close();
        openCreditsAddedDialog(this.dialog, goldAdded, subtitle, rows);
      } catch (error) {
        // The purchase completed and finalizePurchase has already toasted success — only the
        // receipt screen failed. Close the drawer rather than let the outer catch contradict that
        // with "purchase failed".
        this.logService.error(
          `Apple purchase ${purchaseId} completed but its receipt failed: ${error}`,
        );
        this.dialogRef?.close();
      }
    } catch {
      this.notifyService.notify(NotificationEnum.error, "errorOccurred", "purchaseFail");
    } finally {
      // finally runs after the early cancel return above, so buyingPackId is always cleared.
      this.buyingPackId.set(null);
    }
  }

  // --- Use Platinum Credits ---

  protected chipAmount(percent: number): number {
    const total = this.totalPlatinum();
    return percent >= 1 ? total : Math.round(total * percent);
  }

  protected isChipActive(percent: number): boolean {
    return (
      this.hasPlatinumBalance() &&
      this.platinumAmount() > 0 &&
      this.platinumAmount() === this.chipAmount(percent)
    );
  }

  protected onChipClick(percent: number): void {
    if (!this.hasPlatinumBalance()) {
      return;
    }
    this.setPlatinumAmount(this.chipAmount(percent));
  }

  protected onSliderInput(event: Event): void {
    this.setPlatinumAmount(Number((event.target as HTMLInputElement).value));
  }

  protected onAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value;
    // type="number" reports value === "" both for a genuinely cleared field and for a transient
    // invalid keystroke state (a trailing ".", a bare "-", a partial "1e"). Only the latter should
    // be ignored - badInput is true only for the transient case, so a real clear still falls
    // through to setPlatinumAmount(0) below.
    if (raw === "" && input.validity.badInput) {
      return;
    }
    const parsed = Number(raw);
    const value = Number.isFinite(parsed) ? parsed : 0;
    // A typed decimal waits for onAmountCommit: rounding it here wrote the whole figure back into
    // the field mid-keystroke ("12.5" became "13" under the caret).
    if (!Number.isInteger(value)) {
      return;
    }
    this.setPlatinumAmount(value);
  }

  /** The field was committed (blur or Enter): round a typed decimal to a whole credit now. */
  protected onAmountCommit(event: Event): void {
    const parsed = Number((event.target as HTMLInputElement).value);
    if (Number.isFinite(parsed) && !Number.isInteger(parsed)) {
      this.setPlatinumAmount(parsed);
    }
  }

  /** Rounds to a whole credit and keeps it within 0..balance. */
  private clampPlatinumAmount(value: number): number {
    return Math.max(0, Math.min(this.totalPlatinum(), Math.round(value)));
  }

  private setPlatinumAmount(value: number): void {
    this.prefilledFromUnresolved = false;
    const clamped = this.clampPlatinumAmount(value);
    // Same amount keeps the same id: re-picking it after an unknown-outcome error (a 5xx that came
    // back after the backend committed) must retry the same conversion, not start a second one.
    // A changed amount asks CreditsService, which returns an unresolved conversion's id on a match.
    if (clamped !== this.platinumAmount()) {
      this.conversionId.set(this.creditsService.conversionIdFor(this.accountId(), clamped));
    }
    this.platinumAmount.set(clamped);
    // platinumAmount.set() doesn't emit when clamped equals the current value, so [value] on the
    // amount field is skipped by Angular and the input keeps the rejected keystroke (e.g. typing
    // above max) while the rest of the UI shows the clamped figure. Only write the DOM back when
    // a clamp actually happened, mirroring gold-platinum-converter.component.ts's setAmount().
    if (clamped !== value) {
      this.amountEl().nativeElement.value = clamped ? String(clamped) : "";
    }
  }

  private accountId(): string {
    return this.creditsService.accounts()[0]?._id ?? "";
  }

  /** Footer "Convert Credits" click: confirm, then run the conversion — same confirm-before-
   *  convert gate the standalone converter drawer used, now that this tab replaces it. */
  protected convert(): void {
    if (this.convertDisabled()) {
      return;
    }
    this.confirmOpen.set(true);
    this.dialog
      .open(DialogWrapperComponent, {
        panelClass: "redeem-no-background-dialog",
        disableClose: true,
        data: {
          title: "confirmConversion",
          showBackButton: false,
          component: ConversionConfirmationComponent,
          componentInputs: { amount: this.platinumAmount() },
        },
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        this.confirmOpen.set(false);
        if (!confirmed) {
          return;
        }
        this.converting.set(true);
        void this.runConversion();
      });
  }

  private async runConversion(): Promise<void> {
    const accountId = this.accountId();
    const platinumUsed = this.platinumAmount();
    const conversionId = this.conversionId();

    // Scoped to the request alone: once convertCredits resolves, the conversion has committed
    // server-side, so nothing after it (e.g. reading the response) may be reported as a failure.
    let result: IConversionResponse;
    try {
      result = await this.creditsService.convertCredits({
        accountId,
        amount: platinumUsed,
        conversionId,
      });
    } catch (error) {
      if (error instanceof ConversionError && !error.outcomeUnknown) {
        this.converting.set(false);
        // Definitive rejection (e.g. insufficient balance): show the server's reason.
        this.notifyService.notify(
          NotificationEnum.error,
          this.i18n.t("errorOccurred"),
          error.serverMessage,
          false,
        );
        this.dialogRef?.close();
        return;
      }
      // 5xx/gateway or network error: the outcome is unknown. A gateway timeout (e.g. 504) can
      // arrive after the conversion committed; raw error text goes to the log only.
      this.logService.error(`Conversion ${conversionId} request failed: ${error}`);
      void this.resolveUnknownOutcome(conversionId, platinumUsed);
      return;
    }
    this.converting.set(false);
    this.showConversionReceipt(result, platinumUsed, conversionId);
  }

  /**
   * After an unknown outcome: refresh, and if the conversion turns out to have committed, end with
   * its receipt as a success would (#6248) - not an error over a balance already spent, which left
   * Convert disabled and "Balance after" negative. Otherwise the error stands and the drawer stays
   * open with the same conversionId (CreditsService also keeps it for a reopened drawer), now over
   * the refreshed balances. Convert stays disabled ("converting") until this is known.
   */
  private async resolveUnknownOutcome(conversionId: string, platinumUsed: number): Promise<void> {
    try {
      const committed = await this.creditsService.resolveConversion(conversionId);
      if (committed) {
        this.converting.set(false);
        this.showConversionReceipt(committed, platinumUsed, conversionId);
        return;
      }
    } catch (error) {
      // Unexpected (resolveConversion handles a failed refresh itself): the outcome stays
      // unknown, so the error below stands, rather than an unhandled rejection leaving Convert
      // stuck on "processing".
      this.logService.error(`Resolving conversion ${conversionId} failed: ${error}`);
    }
    this.converting.set(false);
    this.notifyService.notify(NotificationEnum.error, "errorOccurred", "somethingWentWrong");
  }

  private showConversionReceipt(
    result: ResolvedConversion,
    platinumUsed: number,
    conversionId: string,
  ): void {
    // Only the types assert the payload's shape (no runtime validation in convertCredits), so
    // check each leg before reading it. A malformed 2xx still committed: it gets a receipt built
    // from what's known locally, with the Gold headline left blank rather than guessed.
    // `fallback` is only evaluated when the field is missing.
    const missing: string[] = [];
    const num = <T extends number | null>(field: string, value: unknown, fallback: () => T) => {
      if (isFiniteNumber(value)) {
        return value;
      }
      missing.push(field);
      return fallback();
    };
    const goldAdded = num("deposit.amount", result?.deposit?.amount, () => null);
    // convertCredits tries to refresh balances before resolving, so the local total is usually
    // current; it can lag if that refresh failed (convertCredits only warns and toasts then).
    const newGoldBalance = num("deposit.balanceAfter", result?.deposit?.balanceAfter, () =>
      this.creditsService.totalGoldCredits(),
    );
    const reportedPlatinumUsed = num(
      "withdrawal.amount",
      result?.withdrawal?.amount,
      () => platinumUsed,
    );
    let transactionId = conversionId;
    if (typeof result?.conversionId === "string" && result.conversionId) {
      transactionId = result.conversionId;
    } else {
      missing.push("conversionId");
    }
    if (missing.length) {
      this.logService.warning(
        `Conversion ${conversionId} committed but its outcome is missing ${missing.join(", ")}; ` +
          "receipt built from local values",
      );
    }
    const { subtitle, rows } = buildConvertReceipt(this.i18n, {
      goldAdded,
      platinumUsed: reportedPlatinumUsed,
      newGoldBalance,
      transactionId,
    });
    this.dialogRef?.close();
    openCreditsAddedDialog(this.dialog, goldAdded, subtitle, rows);
  }
}
